import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { deliveryClient } from '../../../lib/connect/clients';
// Contact lifecycle (SAD-433): deactivating hides a person from new selections
// and ordinary lists, and deletes nothing — employment history, touchpoints and
// staffing stay exactly as they were.
/**
 * The contact list including deactivated people, for the explicit "Show
 * inactive" view. Ordinary lists keep using contactHooks.useList, which omits
 * them. Disabled until asked for so the default view costs one fetch.
 */ export function useContactsWithInactive(req, enabled) {
    return useQuery({
        queryKey: [
            'contact',
            'with inactive',
            req.companyId,
            req.search
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listContacts({
                companyId: req.companyId,
                search: req.search,
                includeInactive: true
            });
            return {
                items: resp.contacts
            };
        },
        enabled
    });
}
function useSetContactActive(active) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>active ? deliveryClient.reactivateContact(req) : deliveryClient.retireContact(req),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'contact'
                ]
            });
            toast.success(active ? 'Contact reactivated' : 'Contact deactivated');
        },
        onError: (err)=>{
            const fallback = active ? 'Failed to reactivate contact' : 'Failed to deactivate contact';
            toast.error(err instanceof Error ? err.message : fallback);
        }
    });
}
export const useRetireContact = ()=>useSetContactActive(false);
export const useReactivateContact = ()=>useSetContactActive(true);
/** A contact's touchpoints, each with the affiliation frozen when it was recorded. */ export function useContactTouchpoints(contactId, companyId) {
    return useQuery({
        queryKey: [
            'contact touchpoints',
            contactId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listContactTouchpoints({
                companyId,
                contactId: contactId ?? ''
            });
            return resp.touchpoints;
        },
        enabled: !!contactId,
        staleTime: 30000
    });
}
